import { api } from './client';

// ==================== Types ====================

export type AgeZone = 'normal' | 'danger' | 'critical' | 'extreme';
export type DocReturnStatus = 'NOT_RETURNED' | 'CLIENT_DELIVERED' | 'CLIENT_RETURNED' | 'RETURNED' | 'ARCHIVED' | 'NON_RETURNABLE';

export interface UnsurrenderedDocument {
  id: string;
  type: 'CONTRACT' | 'AMENDMENT' | 'ACT';
  systemNumber: number;
  displayNumber: string;
  date: string;
  ageDays: number;
  ageZone: AgeZone;
  returnStatus: DocReturnStatus;
  barcode: string;
}

export interface ClientDocumentsSummary {
  total: number;
  danger: number;
  critical: number;
}

export interface ClientDocumentsResponse {
  summary: ClientDocumentsSummary;
  documents: UnsurrenderedDocument[];
}

export interface SetStatusResult {
  success: boolean;
  document: UnsurrenderedDocument | null;
}

// === Типы для всех документов клиента ===

export interface StatusHistoryEntry {
  id: string;
  returnStatus: string;
  employeeName1C: string | null;
  keycloakUserId: string | null;
  recordedAt: string;
  wasAccepted: boolean;
  wasArchived: boolean;
  wasSubmitted: boolean;
  source: string;
}

export interface ClientDocument {
  id: string;
  type: 'CONTRACT' | 'AMENDMENT' | 'INVOICE' | 'ACT';
  systemNumber: number;
  displayNumber: string;
  date: string;
  amount: number | null;
  returnStatus: string;
  barcode: string;
  paymentState?: 'PAID' | 'UNPAID';
  statusHistory: StatusHistoryEntry[];
}

export interface AllDocumentsResponse {
  documents: ClientDocument[];
  total: number;
  counts: {
    contracts: number;
    amendments: number;
    invoices: number;
    acts: number;
  };
}

// ==================== API ====================

export const clientDocumentsApi = {
  /**
   * Получить несданные документы клиента
   */
  getUnsurrenderedDocuments: async (clientId: number): Promise<ClientDocumentsResponse> => {
    const { data } = await api.get(`/clients/${clientId}/unsurrendered-documents`);
    return data;
  },

  /**
   * Изменить статус документа
   */
  setDocumentStatus: async (
    clientId: number,
    docType: 'CONTRACT' | 'AMENDMENT' | 'ACT',
    systemNumber: number,
    status: 'NOT_RETURNED' | 'CLIENT_DELIVERED' | 'CLIENT_RETURNED',
  ): Promise<SetStatusResult> => {
    const { data } = await api.post(
      `/clients/${clientId}/documents/${docType}/${systemNumber}/status`,
      { status },
    );
    return data;
  },

  /**
   * Получить ВСЕ документы клиента за период (по умолчанию - 3 месяца)
   * Включает историю статусов для каждого документа
   */
  getAllDocuments: async (
    clientId: number,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<AllDocumentsResponse> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const { data } = await api.get(`/clients/${clientId}/documents`, { params });
    return data;
  },

  // ==================== PDF Methods ====================

  /**
   * Скачать PDF договора
   */
  getContractPdf: async (id: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/contracts/${id}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Скачать PDF допсоглашения
   */
  getAmendmentPdf: async (id: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/amendments/${id}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Скачать PDF акта
   */
  getActPdf: async (id: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/acts/${id}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Скачать PDF счёта
   */
  getInvoicePdf: async (id: string, signerId?: string): Promise<Blob> => {
    const { data } = await api.get(`/invoices/${id}/pdf`, {
      params: signerId ? { signerId } : {},
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Универсальный метод для скачивания PDF документа (UnsurrenderedDocument)
   */
  getDocumentPdf: async (doc: UnsurrenderedDocument, signerId?: string): Promise<Blob> => {
    switch (doc.type) {
      case 'CONTRACT':
        return clientDocumentsApi.getContractPdf(doc.id, signerId);
      case 'AMENDMENT':
        return clientDocumentsApi.getAmendmentPdf(doc.id, signerId);
      case 'ACT':
        return clientDocumentsApi.getActPdf(doc.id, signerId);
    }
  },

  /**
   * Универсальный метод для скачивания PDF любого документа (ClientDocument)
   */
  getAnyDocumentPdf: async (doc: ClientDocument, signerId?: string): Promise<Blob> => {
    switch (doc.type) {
      case 'CONTRACT':
        return clientDocumentsApi.getContractPdf(doc.id, signerId);
      case 'AMENDMENT':
        return clientDocumentsApi.getAmendmentPdf(doc.id, signerId);
      case 'INVOICE':
        return clientDocumentsApi.getInvoicePdf(doc.id, signerId);
      case 'ACT':
        return clientDocumentsApi.getActPdf(doc.id, signerId);
    }
  },

  /**
   * Получить PDF как base64 для вложения в email
   */
  getDocumentPdfAsBase64: async (doc: UnsurrenderedDocument, signerId?: string): Promise<string> => {
    const blob = await clientDocumentsApi.getDocumentPdf(doc, signerId);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },
};
